'use client';

import { useState, useEffect, useMemo } from 'react';
import { Box, Grid, TextField, CircularProgress, Button, MenuItem, Typography, InputAdornment } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import { formatUpperCase } from '../../../utils/formatTitleCase';
import PickerField from '../../../components/PickerField';
import PickerFieldTable from '../../../components/PickerFieldTable';
import usePickerPagination from '../../../hooks/usePickerPagination';

import { createOrUpdateTireBatch, getTireBatchList } from '../../../stores/tireBatchStore';

import { useCompanyDataList, getCompanyById } from '../../../stores/companyStore';
import { useTireSpecDataList } from '../../../stores/tireSpecStore';
import { useWarehouseDataList } from '../../../stores/warehouseStore';

const getInitials = (name) => {
  if (!name) return '';
  const cleaned = name.replace(/PT\.?|TBK\.?|CV\.?/gi, '').trim();
  const words = cleaned.split(' ').filter(Boolean);
  if (words.length >= 2) {
    return words
      .map((w) => w[0])
      .join('')
      .substring(0, 3)
      .toUpperCase();
  }
  const consonants = cleaned.replace(/[aeiouAEIOU\s]/g, '');
  if (consonants.length >= 3) return consonants.substring(0, 3).toUpperCase();
  return cleaned.substring(0, 3).toUpperCase();
};

const TireReceivingAdd = ({ open, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    company_id: '',
    batch_number: '',
    tire_brand_id: null,
    tire_brand_name: '',
    tire_size_id: null,
    tire_size_code: '',
    tire_size_code_internal: '',
    tire_spec_id: null,
    tire_spec_name: '',
    tire_spec_pattern: '',
    original_tread_depth: '',
    receive_date: dayjs().format('YYYY-MM-DD'),
    supplier: '',
    unit_price: '',
    condition: '',
    warehouse_id: null,
    warehouse_name: '',
    status: ''
  });

  const [loading, setLoading] = useState(false);
  const [qty, setQty] = useState('1');
  const [qtyError, setQtyError] = useState('');
  const [serialNumbers, setSerialNumbers] = useState(['']);

  const [inventoryList, setInventoryList] = useState([]);
  const [autoBatchNumber, setAutoBatchNumber] = useState('');

  const handleQtyChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^[0-9\b]+$/.test(value)) {
      setQty(value);
      const num = parseInt(value, 10);
      if (num > 200) {
        setQtyError(t('tireReceiving.maxQuantity', 'Maximum 200 serial numbers allowed.'));
        setSerialNumbers([]);
      } else if (!isNaN(num) && num > 0) {
        setQtyError('');
        const newArr = [...serialNumbers];
        if (num > newArr.length) {
          newArr.push(...Array(num - newArr.length).fill(''));
        } else {
          newArr.length = num;
        }
        setSerialNumbers(newArr);
      } else {
        setQtyError('');
        setSerialNumbers([]);
      }
    }
  };

  const handleSnChange = (index, value) => {
    const newSn = [...serialNumbers];
    newSn[index] = formatUpperCase(value);
    setSerialNumbers(newSn);
  };

  const [bulkText, setBulkText] = useState('');
  const [showBulkPaste, setShowBulkPaste] = useState(false);

  const handleBulkPaste = (e) => {
    setBulkText(e.target.value);
  };

  const handleProcessBulk = () => {
    if (!bulkText.trim()) return;

    const lines = bulkText
      .split(/\r?\n/)
      .map((l) => formatUpperCase(l.trim()))
      .filter((l) => l !== '');
    const uniqueLines = [...new Set(lines)];
    const existing = serialNumbers.filter((s) => s.trim() !== '');
    const toAdd = uniqueLines.filter((l) => !existing.includes(l));

    if (toAdd.length === 0) {
      notify({ message: t('tireReceiving.noNewSerials', 'No new or valid serial numbers found in the pasted text.'), severity: 'warning' });
      return;
    }

    const combined = [...existing, ...toAdd];
    if (combined.length > 200) {
      notify({ message: t('tireReceiving.cannotAddMore', 'Cannot add more than 200 serial numbers.'), severity: 'error' });
      return;
    }

    setSerialNumbers(combined);
    setQty(String(combined.length));
    setBulkText('');
    setQtyError('');
    notify({
      message: t('tireReceiving.addedSerials', `Added`) + toAdd.length + t('tireReceiving.serialNumbers', 'Serial Numbers'),
      severity: 'success'
    });
  };

  const handleAddField = () => {
    if (serialNumbers.length >= 200) {
      notify({ message: t('tireReceiving.cannotAddMore', 'Cannot add more than 200 serial numbers.'), severity: 'error' });
      return;
    }
    const newArr = [...serialNumbers, ''];
    setSerialNumbers(newArr);
    setQty(String(newArr.length));
    setQtyError('');
  };

  const conditionOptions = [
    { value: 'New', label: 'New' },
    { value: 'Retread', label: 'Retread' },
    { value: 'Used', label: 'Used' }
  ];

  useEffect(() => {
    if (!open) return;

    const fetch = async () => {
      let defaultCompanyId = '';
      try {
        const stored = localStorage.getItem('companyId');
        if (stored) {
          defaultCompanyId = String(stored);
        } else {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const u = JSON.parse(userStr);
            defaultCompanyId = u?.company ? String(u.company) : '';
          }
        }
      } catch (e) {
        console.error('auto select company error', e);
      }

      let defaultBatch = '';
      if (defaultCompanyId) {
        const comp = await getCompanyById(defaultCompanyId);
        const name = comp?.name || '';
        const initials = getInitials(name);
        const res = await getTireBatchList([{ value: defaultCompanyId }]);
        setInventoryList(res);
        let nextNum = 1;
        if (res && res.length > 0) {
          const samePrefix = res.filter((r) => {
            const bn = r.batch_number || r.join?.tire_item_batch_number;
            return bn && bn.startsWith(`${initials}-`);
          });
          if (samePrefix.length > 0) {
            samePrefix.sort((a, b) => {
              const bna = a.batch_number || a.join?.tire_item_batch_number;
              const bnb = b.batch_number || b.join?.tire_item_batch_number;
              const numA = parseInt(bna.split('-')[1], 10) || 0;
              const numB = parseInt(bnb.split('-')[1], 10) || 0;
              return numB - numA;
            });
            const lastBatch = samePrefix[0].batch_number || samePrefix[0].join?.tire_item_batch_number;
            const numStr = lastBatch.split('-')[1];
            if (numStr && !isNaN(numStr)) {
              nextNum = parseInt(numStr, 10) + 1;
            }
          }
        }
        const formattedNum = String(nextNum).padStart(6, '0');
        defaultBatch = initials ? `${initials}-${formattedNum}` : '';
        setAutoBatchNumber(defaultBatch);
      }

      setInitialValues({
        company_id: defaultCompanyId,
        batch_number: defaultBatch,
        tire_brand_id: null,
        tire_brand_name: '',
        tire_size_id: null,
        tire_size_code: '',
        tire_size_code_internal: '',
        tire_spec_id: null,
        tire_spec_name: '',
        tire_spec_pattern: '',
        original_tread_depth: '',
        receive_date: dayjs().format('YYYY-MM-DD'),
        supplier: '',
        unit_price: '',
        condition: '',
        warehouse_id: null,
        warehouse_name: '',
        status: ''
      });
      setQty('1');
      setSerialNumbers(['']);
      setBulkText('');
      setLoading(false);
    };

    fetch();
  }, [open, notify, t]);

  // // Computed property to check if current receive_date is backdated
  // const isBackdate = initialValues.receive_date ? dayjs(initialValues.receive_date).startOf('day').isBefore(dayjs().startOf('day')) : false;
  // // Note: we'll use formik.values.receive_date inside the render directly

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      company_id: Yup.string().required(t('validation.required', 'Required')),
      batch_number: Yup.string()
        .required(t('validation.required', 'Required'))
        .test('check-duplicate', 'Batch Number already exists in inventory.', function (value) {
          const receiveDate = this.parent.receive_date;
          const currentIsBackdate = receiveDate ? dayjs(receiveDate).startOf('day').isBefore(dayjs().startOf('day')) : false;

          if (currentIsBackdate && value) {
            const exists = inventoryList.some((item) => item.batch_number === value);
            if (exists) {
              return false;
            }
          }
          return true;
        }),
      tire_spec_id: Yup.number().nullable().required(t('validation.required', 'Required')),
      receive_date: Yup.string().required(t('validation.required', 'Required')),
      supplier: Yup.string().required(t('validation.required', 'Required')),
      unit_price: Yup.string().required(t('validation.required', 'Required')),
      condition: Yup.string().required(t('validation.required', 'Required'))
    });
  }, [t, inventoryList]);

  const handleClose = () => {
    formik.resetForm();
    setQty('1');
    setSerialNumbers(['']);
    setBulkText('');
    setQtyError('');
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          company_id: Number(values.company_id),
          tire_spec_id: Number(values.tire_spec_id),
          batch_number: values.batch_number,
          supplier: values.supplier,
          unit_price: values.unit_price.replace(/[^0-9]/g, ''),
          base_condition: values.condition,
          receive_date: values.receive_date,
          warehouse_id: Number(values.warehouse_id),
          warehouse_name: values.warehouse_name,
          batch_data: serialNumbers
            .filter((sn) => sn.trim() !== '')
            .map((sn) => ({
              serial_number: formatUpperCase(sn)
            }))
        };

        await createOrUpdateTireBatch(payload);
        notify({ message: t('tireReceiving.success', 'Tire Batch saved successfully'), severity: 'success' });

        formik.resetForm();
        setSerialNumbers(['']);
        setQty('1');
        setBulkText('');
        setQtyError('');
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.message || err?.response?.data?.error || 'Failed to save data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const currentIsBackdate = formik.values.receive_date
    ? dayjs(formik.values.receive_date).startOf('day').isBefore(dayjs().startOf('day'))
    : false;

  useEffect(() => {
    if (open && !currentIsBackdate && formik.values.batch_number !== autoBatchNumber && autoBatchNumber) {
      formik.setFieldValue('batch_number', autoBatchNumber);
    }
  }, [currentIsBackdate, autoBatchNumber, formik.values.batch_number, formik, open]);

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const tireSpecPickerParams = usePickerPagination({
    fetchHook: useTireSpecDataList,
    buildArgs: (state) => [
      state.page,
      state.pageSize,
      formik.values.company_id ? [{ value: formik.values.company_id, label: formik.values.company_id }] : [],
      state.searchKey,
      state.searchValue,
      []
    ],
    searchKey: 'spec_name',
    formatOption: (item) => {
      const sn = item.spec_name || '';
      const p = item.pattern || '';
      const b = item.join?.tire_brand_name || item.tire_brand_name || '';
      const s =
        item.join?.tire_size_code_internal || item.tire_size_code_internal || item.join?.tire_size_code || item.tire_size_code || '';
      const otd = item.original_tread_depth ? String(item.original_tread_depth) : '';
      const labelParts = [sn, p, b, s, otd].filter(Boolean).join(' - ');
      return {
        ...item,
        value: String(item.id),
        label: labelParts || String(item.id)
      };
    }
  });

  const warehousePickerParams = usePickerPagination({
    fetchHook: useWarehouseDataList,
    buildArgs: (state) => [
      state.page,
      state.pageSize,
      formik.values.company_id ? [{ value: formik.values.company_id, label: formik.values.company_id }] : [],
      state.searchKey,
      state.searchValue
    ],
    searchKey: 'name',
    formatOption: (item) => ({
      ...item,
      value: String(item.id),
      label: item.name || String(item.id)
    })
  });

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  const specColumns = [
    { field: 'spec_name', headerName: t('specification.spec_name', 'Spec Name'), width: 200 },
    {
      field: 'brand_name',
      headerName: t('specification.brand', 'Brand'),
      width: 150,
      renderCell: (raw) => raw?.join?.tire_brand_name || raw?.join?.brand_name || raw?.tire_brand_name || raw?.brand_name || '-'
    },
    {
      field: 'size_code',
      headerName: t('specification.size', 'Size'),
      width: 150,
      renderCell: (raw) =>
        raw?.join?.tire_size_code_internal ||
        raw?.join?.size_code_internal ||
        raw?.join?.tire_size_code ||
        raw?.join?.size_code ||
        raw?.tire_size_code_internal ||
        raw?.size_code ||
        raw?.tire_size_code ||
        raw?.size_code_internal ||
        '-'
    },
    { field: 'pattern', headerName: t('specification.pattern', 'Pattern'), width: 150 },
    { field: 'original_tread_depth', headerName: t('specification.originalTreadDepth', 'OTD'), width: 100 }
  ];

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={t('tireReceiving.receiveTire', 'Receive Tire')}
      fullScreenMode={true}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.5}>
              <Grid item size={{ xs: 12, md: 4 }}>
                <PickerField
                  label={t('general.company', 'Company')}
                  value={formik.values.company_id}
                  onChange={async (val) => {
                    formik.setFieldValue('company_id', val ?? '');
                    formik.setFieldValue('tire_spec_id', null);
                    formik.setFieldValue('tire_spec_name', '');
                    formik.setFieldValue('tire_brand_id', null);
                    formik.setFieldValue('tire_brand_name', '');
                    formik.setFieldValue('tire_size_id', null);
                    formik.setFieldValue('tire_size_code', '');
                    formik.setFieldValue('tire_size_code_internal', '');
                    formik.setFieldValue('warehouse_id', null);
                    formik.setFieldValue('warehouse_name', '');

                    if (val) {
                      setLoading(true);
                      const companyObj = companyPickerParams.options.find((c) => String(c.value) === String(val));
                      let cName = companyObj ? companyObj.label : '';
                      if (!cName) {
                        const comp = await getCompanyById(val);
                        cName = comp?.name || '';
                      }
                      const initials = getInitials(cName);

                      const res = await getTireBatchList([{ value: val }]);
                      setInventoryList(res);

                      let nextNum = 1;
                      if (res && res.length > 0) {
                        const samePrefix = res.filter((r) => {
                          const bn = r.batch_number || r.join?.tire_item_batch_number;
                          return bn && bn.startsWith(`${initials}-`);
                        });
                        if (samePrefix.length > 0) {
                          samePrefix.sort((a, b) => {
                            const bna = a.batch_number || a.join?.tire_item_batch_number;
                            const bnb = b.batch_number || b.join?.tire_item_batch_number;
                            const numA = parseInt(bna.split('-')[1], 10) || 0;
                            const numB = parseInt(bnb.split('-')[1], 10) || 0;
                            return numB - numA;
                          });
                          const lastBatch = samePrefix[0].batch_number || samePrefix[0].join?.tire_item_batch_number;
                          const numStr = lastBatch.split('-')[1];
                          if (numStr && !isNaN(numStr)) {
                            nextNum = parseInt(numStr, 10) + 1;
                          }
                        }
                      }
                      const formattedNum = String(nextNum).padStart(6, '0');
                      const newBatch = initials ? `${initials}-${formattedNum}` : '';

                      setAutoBatchNumber(newBatch);

                      const checkBackdate = formik.values.receive_date
                        ? dayjs(formik.values.receive_date).startOf('day').isBefore(dayjs().startOf('day'))
                        : false;

                      if (!checkBackdate) {
                        formik.setFieldValue('batch_number', newBatch);
                      }
                      setLoading(false);
                    } else {
                      setInventoryList([]);
                      setAutoBatchNumber('');
                      formik.setFieldValue('batch_number', '');
                    }
                  }}
                  error={formik.touched.company_id && Boolean(formik.errors.company_id)}
                  helperText={formik.touched.company_id && formik.errors.company_id}
                  options={companyPickerParams.options}
                  loading={companyPickerParams.loading}
                  onSearch={companyPickerParams.handleSearch}
                  onScrollEnd={companyPickerParams.handleScrollEnd}
                  getOptionId={(o) => o?.value ?? o?.id ?? ''}
                  getOptionLabel={(o) => o?.label ?? o?.name ?? ''}
                  required
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    label={`${t('tireReceiving.receiveDate', 'Receive Date')} *`}
                    value={formik.values.receive_date ? dayjs(formik.values.receive_date) : null}
                    onChange={(newValue) => {
                      formik.setFieldValue('receive_date', newValue ? newValue.format('YYYY-MM-DD') : '');
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        id: 'receive_date',
                        name: 'receive_date',
                        error: formik.touched.receive_date && !!formik.errors.receive_date,
                        helperText: formik.touched.receive_date && formik.errors.receive_date,
                        InputLabelProps: { sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('tireReceiving.batchNo', 'Batch Number')}
                  fullWidth
                  {...formik.getFieldProps('batch_number')}
                  onChange={(e) => {
                    formik.setFieldValue('batch_number', formatUpperCase(e.target.value));
                  }}
                  disabled={!currentIsBackdate}
                  error={formik.touched.batch_number && Boolean(formik.errors.batch_number)}
                  helperText={
                    (formik.touched.batch_number && formik.errors.batch_number) ||
                    (!currentIsBackdate
                      ? t('tireReceiving.autoGenerated', 'Auto-generated (Read-only for current/future dates)')
                      : t('tireReceiving.manualInput', 'Manual input allowed (Backdated)'))
                  }
                  slotProps={{
                    input: {
                      readOnly: !currentIsBackdate
                    }
                  }}
                  InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <PickerFieldTable
                  label={t('specification.spec_name', 'Spec Name')}
                  dialogTitle={t('tireReceiving.selectSpec', 'Select Specification')}
                  value={formik.values.tire_spec_id}
                  onChange={(valId) => {
                    formik.setFieldValue('tire_spec_id', valId || null);
                    if (valId) {
                      const val = tireSpecPickerParams.options.find((o) => String(o.id) === String(valId));
                      if (val) {
                        formik.setFieldValue('tire_spec_name', val.spec_name || '');
                        formik.setFieldValue('tire_spec_pattern', val.pattern || '');

                        formik.setFieldValue('tire_brand_id', val.tire_brand || val.tire_brand_id || null);
                        formik.setFieldValue(
                          'tire_brand_name',
                          val.join?.tire_brand_name || val.join?.brand_name || val.tire_brand_name || val.brand_name || ''
                        );

                        formik.setFieldValue('tire_size_id', val.tire_size || val.tire_size_id || null);
                        formik.setFieldValue(
                          'tire_size_code',
                          val.join?.tire_size_code || val.join?.size_code || val.tire_size_code || val.size_code || ''
                        );

                        formik.setFieldValue(
                          'tire_size_code_internal',
                          val.join?.tire_size_code_internal ||
                            val.join?.size_code_internal ||
                            val.tire_size_code_internal ||
                            val.size_code_internal ||
                            ''
                        );
                        formik.setFieldValue('original_tread_depth', val.original_tread_depth ?? '');
                      }
                    } else {
                      formik.setFieldValue('tire_spec_name', '');
                      formik.setFieldValue('tire_spec_pattern', '');
                      formik.setFieldValue('tire_brand_id', null);
                      formik.setFieldValue('tire_brand_name', '');
                      formik.setFieldValue('tire_size_id', null);
                      formik.setFieldValue('tire_size_code', '');
                      formik.setFieldValue('tire_size_code_internal', '');
                      formik.setFieldValue('original_tread_depth', '');
                    }
                  }}
                  columns={specColumns}
                  {...tireSpecPickerParams}
                  getOptionId={(o) => o?.value ?? o?.id ?? ''}
                  getOptionLabel={(o) => o?.label ?? o?.pattern ?? o?.spec_name ?? ''}
                  disabled={!formik.values.company_id}
                  required
                  error={formik.touched.tire_spec_id && Boolean(formik.errors.tire_spec_id)}
                  helperText={formik.touched.tire_spec_id && formik.errors.tire_spec_id}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('specification.brand', 'Brand')}
                  value={formik.values.tire_brand_name || ''}
                  size="medium"
                  fullWidth
                  placeholder={!formik.values.tire_spec_id ? t('tireReceiving.pleaseSelect', 'Please select a specification first') : ''}
                  slotProps={{
                    input: {
                      readOnly: true
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('tireReceiving.autoFillInfo', 'This field is auto-filled when you select a specification.')}
                  FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('specification.size', 'Size')}
                  value={formik.values.tire_size_code_internal || formik.values.tire_size_code || ''}
                  size="medium"
                  fullWidth
                  placeholder={!formik.values.tire_spec_id ? t('tireReceiving.pleaseSelect', 'Please select a specification first') : ''}
                  slotProps={{
                    input: {
                      readOnly: true
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('tireReceiving.autoFillInfo', 'This field is auto-filled when you select a specification.')}
                  FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('specification.pattern', 'Pattern')}
                  value={formik.values.tire_spec_pattern || ''}
                  size="medium"
                  fullWidth
                  placeholder={!formik.values.tire_spec_id ? t('tireReceiving.pleaseSelect', 'Please select a specification first') : ''}
                  slotProps={{
                    input: {
                      readOnly: true
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('tireReceiving.autoFillInfo', 'This field is auto-filled when you select a specification.')}
                  FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('specification.originalTreadDepth', 'Original Tread Depth (mm)')}
                  value={formik.values.original_tread_depth || ''}
                  size="medium"
                  fullWidth
                  placeholder={!formik.values.tire_spec_id ? t('tireReceiving.pleaseSelect', 'Please select a specification first') : ''}
                  slotProps={{
                    input: {
                      readOnly: true
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('tireReceiving.autoFillInfo', 'This field is auto-filled when you select a specification.')}
                  FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('tireReceiving.supplier', 'Supplier')}
                  name="supplier"
                  value={formik.values.supplier}
                  onChange={(e) => {
                    formik.setFieldValue('supplier', formatUpperCase(e.target.value));
                  }}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  required
                  error={formik.touched.supplier && Boolean(formik.errors.supplier)}
                  helperText={formik.touched.supplier && formik.errors.supplier}
                  InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('tireReceiving.unitPrice', 'Unit Price')}
                  name="unit_price"
                  value={formik.values.unit_price}
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^0-9]/g, '');
                    if (val) {
                      val = Number(val).toLocaleString('id-ID');
                    }
                    formik.setFieldValue('unit_price', val);
                  }}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  required
                  error={formik.touched.unit_price && Boolean(formik.errors.unit_price)}
                  helperText={formik.touched.unit_price && formik.errors.unit_price}
                  InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Rp</InputAdornment>
                  }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  label={t('dashboardAsset.condition', 'Condition')}
                  name="condition"
                  value={formik.values.condition}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  required
                  error={formik.touched.condition && Boolean(formik.errors.condition)}
                  helperText={formik.touched.condition && formik.errors.condition}
                  InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                >
                  {conditionOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <PickerField
                  label={t('warehouse.name', 'Warehouse')}
                  value={formik.values.warehouse_id}
                  onChange={(valId) => {
                    formik.setFieldValue('warehouse_id', valId || null);
                    if (valId) {
                      const val = warehousePickerParams.options.find((o) => String(o.id) === String(valId));
                      if (val) {
                        formik.setFieldValue('warehouse_name', val.name || '');
                      }
                    } else {
                      formik.setFieldValue('warehouse_name', '');
                    }
                  }}
                  options={warehousePickerParams.options}
                  loading={warehousePickerParams.loading}
                  onSearch={warehousePickerParams.handleSearch}
                  onScrollEnd={warehousePickerParams.handleScrollEnd}
                  getOptionId={(o) => o?.value ?? o?.id ?? ''}
                  getOptionLabel={(o) => o?.label ?? o?.name ?? ''}
                  disabled={!formik.values.company_id}
                  required
                  error={formik.touched.warehouse_id && Boolean(formik.errors.warehouse_id)}
                  helperText={formik.touched.warehouse_id && formik.errors.warehouse_id}
                />
              </Grid>
            </Grid>

            {/* Serial Number Section */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="600" color="text.primary" sx={{ letterSpacing: 0.2 }}>
                  {t('tyreItem.serialNumber', 'Serial Number')}s
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Button variant="outlined" color="secondary" onClick={() => setShowBulkPaste(!showBulkPaste)} size="small">
                    {showBulkPaste
                      ? t('tireReceiving.hideBulkReceive', 'Hide Bulk Paste')
                      : t('tireReceiving.bulkPaste', 'Bulk Paste Data')}
                  </Button>
                  <Button variant="outlined" color="primary" onClick={handleAddField} size="small">
                    + {t('tireReceiving.addField', 'Add Field')}
                  </Button>
                  <Typography variant="body2" color="text.secondary" fontWeight="500">
                    {t('tireReceiving.quantity', 'Quantity')}
                  </Typography>
                  <TextField
                    value={qty}
                    onChange={handleQtyChange}
                    size="small"
                    error={!!qtyError}
                    helperText={qtyError}
                    FormHelperTextProps={{
                      sx: { position: 'absolute', top: '100%', right: 0, width: 'max-content', m: 0, mt: 0.5, textAlign: 'right' }
                    }}
                    sx={{
                      width: 80,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        bgcolor: '#ffffff'
                      },
                      '& .MuiInputBase-input': {
                        py: 0.8
                      }
                    }}
                    type="number"
                    placeholder="0"
                    inputProps={{ style: { textAlign: 'center', fontWeight: '500' } }}
                  />
                </Box>
              </Box>

              {showBulkPaste && (
                <Box sx={{ mb: 4, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('tireReceiving.bulkPastSerialNumbers', 'Bulk Paste Serial Numbers')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t(
                      'tireReceiving.copyColumn',
                      'Copy a column of serial numbers from Excel and paste it here to automatically generate fields.'
                    )}
                  </Typography>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="SN001&#10;SN002&#10;SN003"
                    value={bulkText}
                    onChange={handleBulkPaste}
                    sx={{ bgcolor: '#ffffff', mb: 1 }}
                  />
                  <Button variant="contained" color="secondary" onClick={handleProcessBulk} disabled={!bulkText.trim()}>
                    {t('tireReceiving.processBulkPaste', 'Process Bulk Paste')}
                  </Button>
                </Box>
              )}

              {serialNumbers.length > 0 && (
                <Grid container spacing={2.5}>
                  {serialNumbers.map((sn, index) => (
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={`sn-${index}`}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        <Typography
                          variant="caption"
                          fontWeight="700"
                          sx={{ color: '#485D9A', letterSpacing: 0.8, textTransform: 'uppercase', fontSize: '0.7rem' }}
                        >
                          {t('tireReceiving.tire', 'Tire')} #{index + 1}
                        </Typography>
                        <TextField
                          value={sn}
                          onChange={(e) => handleSnChange(index, e.target.value)}
                          size="small"
                          fullWidth
                          placeholder={`SN-${index + 1}`}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              bgcolor: '#F8FAFC',
                              '& fieldset': {
                                borderColor: '#485D9A'
                              },
                              '&:hover fieldset': {
                                borderColor: '#485D9A'
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: '1.5px'
                              }
                            },
                            '& .MuiInputBase-input': {
                              py: 1,
                              color: 'text.main',
                              fontWeight: 500
                            }
                          }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default TireReceivingAdd;
